import { useState } from "react";
import { toast } from "sonner";
import {
  Globe, ChevronDown, Check, Minus, Loader2, Edit2, Trash2,
  Plus, X, Save, AlertCircle, Search, BookOpen, FileText,
  ShieldCheck,
} from "lucide-react";
import { AdminTopbar } from "@/components/admin/AdminLayout";
import { ModulePanel, EditorialStatusBadge } from "@/components/admin/AdminPrimitives";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";
import { useI18n } from "@/lib/i18n";
import { useArticles } from "@/hooks/useArticles";
import { useVerifications } from "@/hooks/useVerifications";
import {
  useTraductionsArticle,
  useCreateTraductionArticle,
  useUpdateTraductionArticle,
  useDeleteTraductionArticle,
} from "@/hooks/useTraductionArticle";
import {
  useTraductionsVerification,
  useCreateTraductionVerification,
  useUpdateTraductionVerification,
  useDeleteTraductionVerification,
} from "@/hooks/useTraductionVerification";
import { ArticleDto } from "@/types/article.types";
import { TraductionArticleDto } from "@/types/traduction-article.types";
import { VerificationDto, TraductionVerificationDto } from "@/types/verification.types";
import {
  LANGUES_CONFIG,
  STATUT_MAP,
  TYPE_CONTENU_FROM_API,
  SIGNATURE_LABEL,
  VERDICT_OPTIONS,
} from "@/lib/enums";

const LANGUES = LANGUES_CONFIG.map((l) => ({
  code: l.code,
  label: l.label,
  flag: l.flag,
  apiCode: l.apiCode,
}));

type TabType = "articles" | "verifications";

// ─── Badge statut langue ──────────────────────────────────────────────────────
function LangStatus({ hasIt }: { hasIt: boolean }) {
  const { t } = useI18n();
  return hasIt ? (
    <span className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10px] font-bold bg-verified/10 text-verified">
      <Check className="h-2.5 w-2.5" /> {t("admin.common.translated")}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10px] font-bold bg-surface text-muted-foreground">
      <Minus className="h-2.5 w-2.5" /> {t("admin.common.missing")}
    </span>
  );
}

// ─── Badge verdict ─────────────────────────────────────────────────────────────
function VerdictBadge({ verdict }: { verdict: string }) {
  const v = VERDICT_OPTIONS.find((vd) => vd.value === verdict);
  if (!v) return <span className="text-xs text-muted-foreground font-mono">{verdict}</span>;
  return (
    <span className={`inline-flex items-center rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${v.color}`}>
      {v.label}
    </span>
  );
}

// ─── Modal traduction article ─────────────────────────────────────────────────
function ArticleTraductionModal({
  articleId,
  articleTitre,
  langue,
  langLabel,
  initial,
  traductionId,
  frTraduction,
  onClose,
  onSaved,
}: {
  articleId: number;
  articleTitre: string;
  langue: string;
  langLabel: string;
  initial?: any;
  traductionId?: number;
  frTraduction?: TraductionArticleDto;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isFr = langue?.toUpperCase() === "FR";
  const { t } = useI18n();
  const create = useCreateTraductionArticle();
  const update = useUpdateTraductionArticle();
  const isEdit = !!traductionId;

  const [fields, setFields] = useState({
    titre: initial?.titre || "",
    sousTitre: initial?.sousTitre || "",
    corps: initial?.corps || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (key: string, val: string) => setFields((p) => ({ ...p, [key]: val }));

  const handleSave = async () => {
    if (!fields.titre.trim()) { setError(t("admin.translations.modal.titleRequired")); return; }
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await update.mutateAsync({ id: traductionId!, payload: { langue, ...fields } });
      } else {
        await create.mutateAsync({ articleId, payload: { langue, ...fields } });
      }
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || t("admin.translations.modal.error"));
    } finally {
      setSaving(false);
    }
  };

  const FIELDS = [
    { key: "titre", label: `${t("admin.article.titre")} *`, rows: 1, mono: false },
    { key: "sousTitre", label: t("admin.article.sousTitre"), rows: 1, mono: false },
    { key: "corps", label: t("admin.article.corps"), rows: 10, mono: true },
  ];

  const showSplit = !isFr && !!frTraduction;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className={`w-full ${showSplit ? "max-w-5xl" : "max-w-2xl"} rounded-lg border border-border bg-background shadow-2xl flex flex-col max-h-[90vh]`}>
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 className="font-serif text-base font-semibold text-foreground">
              {isEdit ? t("admin.common.edit") : t("admin.common.add")} {t("admin.common.translations").toLowerCase()} — {langLabel}
            </h2>
            <p className="text-xs text-muted-foreground line-clamp-1">
              {t("admin.translations.modal.article.ref")} <span className="font-medium text-foreground">« {articleTitre} »</span>
            </p>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 hover:bg-surface text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 rounded-md bg-alert/10 border border-alert/30 px-4 py-2 text-xs text-alert font-medium">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />{error}
            </div>
          )}
          {showSplit && (
            <div className="rounded-md border border-border bg-surface/40 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("admin.translations.split.hint")}
            </div>
          )}
          {FIELDS.map(({ key, label, rows, mono }) => (
            <div key={key} className={showSplit ? "grid gap-4 md:grid-cols-2" : "space-y-1.5"}>
              {showSplit && (
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground">{label} — FR</label>
                  {key === "corps" ? (
                    <div
                      className="prose prose-sm max-w-none rounded-md border border-border bg-surface/50 px-3 py-2 text-foreground/80 min-h-[200px] max-h-[300px] overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: (frTraduction as any)?.[key] || "" }}
                    />
                  ) : (
                    <div className="w-full rounded-md border border-border bg-surface/50 px-3 py-2 text-sm text-foreground/80 min-h-[38px]">
                      {(frTraduction as any)?.[key] || "—"}
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">{showSplit ? `${label} — ${langLabel}` : label}</label>
                {key === "corps" ? (
                  <RichTextEditor value={(fields as any)[key]} onChange={(html) => set(key, html)} />
                ) : rows === 1 ? (
                  <input
                    value={(fields as any)[key]}
                    onChange={(e) => set(key, e.target.value)}
                    autoFocus={key === "titre"}
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
                  />
                ) : (
                  <textarea
                    value={(fields as any)[key]}
                    onChange={(e) => set(key, e.target.value)}
                    rows={rows}
                    className={`w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-foreground resize-none ${mono ? "font-mono" : ""}`}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <button onClick={onClose} className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-surface transition-colors">
            {t("admin.common.cancel")}
          </button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 rounded-md bg-foreground px-5 py-2 text-sm font-semibold text-background hover:bg-foreground/90 disabled:opacity-60 transition-colors">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {t("admin.common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal traduction vérification ───────────────────────────────────────────
function VerifTraductionModal({
  verificationId,
  verifAffirmation,
  langue,
  langLabel,
  initial,
  traductionId,
  onClose,
  onSaved,
}: {
  verificationId: number;
  verifAffirmation: string;
  langue: string;
  langLabel: string;
  initial?: any;
  traductionId?: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const create = useCreateTraductionVerification();
  const update = useUpdateTraductionVerification();
  const isEdit = !!traductionId;

  const { t } = useI18n();

  const FIELDS = [
    { key: "affirmation", label: `${t("admin.factcheck.field.affirmation")}`, rows: 2 },
    { key: "explicationCourte", label: t("admin.factcheck.field.explication"), rows: 2 },
    { key: "ceQuiCircule", label: t("admin.factcheck.field.ceQuiCircule"), rows: 3 },
    { key: "ceQueNousAvonsVerifie", label: t("admin.factcheck.field.ceQueNousAvonsVerifie"), rows: 3 },
    { key: "preuves", label: t("admin.factcheck.field.preuves"), rows: 3 },
    { key: "pourquoiImportant", label: t("admin.factcheck.field.pourquoiImportant"), rows: 2 },
    { key: "partageSecurise", label: t("admin.factcheck.field.partageSecurise2"), rows: 2 },
  ];

  const [fields, setFields] = useState<Record<string, string>>({
    affirmation: initial?.affirmation || "",
    explicationCourte: initial?.explicationCourte || "",
    ceQuiCircule: initial?.ceQuiCircule || "",
    ceQueNousAvonsVerifie: initial?.ceQueNousAvonsVerifie || "",
    preuves: initial?.preuves || "",
    pourquoiImportant: initial?.pourquoiImportant || "",
    partageSecurise: initial?.partageSecurise || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (key: string, val: string) => setFields((p) => ({ ...p, [key]: val }));

  const handleSave = async () => {
    if (!fields.affirmation.trim()) { setError(t("admin.translations.modal.affirmationRequired")); return; }
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await update.mutateAsync({ id: traductionId!, payload: { langue, ...fields } });
      } else {
        await create.mutateAsync({ verificationId, payload: { langue, ...fields } });
      }
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || t("admin.translations.modal.error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl rounded-lg border border-border bg-background shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 className="font-serif text-base font-semibold text-foreground">
              {isEdit ? t("admin.common.edit") : t("admin.common.add")} {t("admin.common.translations").toLowerCase()} — {langLabel}
            </h2>
            <p className="text-xs text-muted-foreground line-clamp-1">
              {t("admin.translations.modal.verif.ref")} <span className="font-medium text-foreground">« {verifAffirmation} »</span>
            </p>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 hover:bg-surface text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 rounded-md bg-alert/10 border border-alert/30 px-4 py-2 text-xs text-alert font-medium">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />{error}
            </div>
          )}
          {FIELDS.map(({ key, label, rows }) => (
            <div key={key} className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">{label}</label>
              <textarea
                value={fields[key] || ""}
                onChange={(e) => set(key, e.target.value)}
                rows={rows}
                autoFocus={key === "affirmation"}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground resize-none"
              />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <button onClick={onClose} className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-surface transition-colors">
            {t("admin.common.cancel")}
          </button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 rounded-md bg-foreground px-5 py-2 text-sm font-semibold text-background hover:bg-foreground/90 disabled:opacity-60 transition-colors">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {t("admin.common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Ligne article avec ses traductions ───────────────────────────────────────
function ArticleTraductionsRow({
  article,
  onRefresh,
}: {
  article: ArticleDto;
  onRefresh: () => void;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const { data: traductionsData, refetch } = useTraductionsArticle(article.id);
  const deleteTraduction = useDeleteTraductionArticle();

  const [modal, setModal] = useState<{
    langue: string;
    langLabel: string;
    trad?: TraductionArticleDto;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TraductionArticleDto | null>(null);

  const tradsList: TraductionArticleDto[] = traductionsData || article.traductions || [];
  const articleTitre = article.traductions?.[0]?.titre || article.slug;
  const disponibles = article.languesDisponibles || [];
  const completion = Math.round((disponibles.length / LANGUES.length) * 100);

  const confirmDelete = () => {
    if (!deleteTarget?.id) return;
    deleteTraduction.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        refetch();
        onRefresh();
      },
      onError: () => {
        toast.error(t("admin.common.deleteError"));
        setDeleteTarget(null);
      },
    });
  };

  const handleSaved = () => { refetch(); onRefresh(); };

  return (
    <div className="border border-border rounded-md overflow-hidden">
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center gap-4 px-4 py-3 hover:bg-surface/60 text-left transition-colors"
      >
        <div className="shrink-0 h-7 w-7 rounded-md bg-surface flex items-center justify-center">
          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-foreground line-clamp-1">{articleTitre}</div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-muted-foreground font-mono">
              {TYPE_CONTENU_FROM_API[article.type] ?? article.type}
            </span>
            <span className="text-[10px] text-muted-foreground">·</span>
            <EditorialStatusBadge status={STATUT_MAP[article.statut] || article.statut} />
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 shrink-0">
          <div className="w-20 h-1.5 rounded-full bg-surface overflow-hidden">
            <div className="h-full rounded-full bg-verified transition-all" style={{ width: `${completion}%` }} />
          </div>
          <span className="text-[10px] font-bold text-muted-foreground w-8 text-right">{completion}%</span>
        </div>
        <div className="flex gap-1 shrink-0">
          {LANGUES.map((l) => (
            <span
              key={l.code}
              className={`text-[9px] font-bold uppercase rounded px-1.5 py-0.5 ${
                disponibles.some((d) => d.toLowerCase() === l.code)
                  ? "bg-verified/15 text-verified"
                  : "bg-surface text-muted-foreground/50"
              }`}
            >
              {l.code}
            </span>
          ))}
        </div>
        <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-border bg-surface/20 px-4 py-4">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            {LANGUES.map((l) => {
              const trad = tradsList.find((t) => t.langue?.toLowerCase() === l.code.toLowerCase());
              const hasIt = !!trad;
              return (
                <div key={l.code} className="rounded-md border border-border bg-background p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="text-base leading-none">{l.flag}</span>
                      <span className="text-xs font-semibold text-foreground">{l.label}</span>
                    </div>
                    <LangStatus hasIt={hasIt} />
                  </div>
                  {trad?.titre && (
                    <p className="text-[10px] text-muted-foreground line-clamp-2 leading-relaxed">{trad.titre}</p>
                  )}
                  <div className="flex gap-1">
                    {hasIt ? (
                      <>
                        <button
                          onClick={() => setModal({ langue: l.apiCode, langLabel: l.label, trad })}
                          className="flex-1 flex items-center justify-center gap-1 rounded-sm border border-border py-1 text-[10px] font-semibold text-muted-foreground hover:text-foreground hover:border-foreground transition-colors"
                        >
                          <Edit2 className="h-2.5 w-2.5" /> {t("admin.translations.action.edit")}
                        </button>
                        <button
                          onClick={() => setDeleteTarget(trad)}
                          className="rounded-sm border border-border p-1 text-muted-foreground hover:text-alert hover:border-alert/50 transition-colors"
                        >
                          <Trash2 className="h-2.5 w-2.5" />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setModal({ langue: l.apiCode, langLabel: l.label })}
                        className="w-full flex items-center justify-center gap-1 rounded-sm border border-dashed border-border py-1.5 text-[10px] font-semibold text-muted-foreground hover:text-foreground hover:border-foreground transition-colors"
                      >
                        <Plus className="h-3 w-3" /> {t("admin.translations.action.add")}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {modal && (
        <ArticleTraductionModal
          articleId={article.id}
          articleTitre={articleTitre}
          langue={modal.langue}
          langLabel={modal.langLabel}
          initial={modal.trad}
          traductionId={modal.trad?.id}
          frTraduction={tradsList.find((t) => t.langue?.toLowerCase() === "fr")}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t("admin.translations.deleteConfirm.title")}
        description={t("admin.translations.deleteConfirm.desc")}
        onConfirm={confirmDelete}
        loading={deleteTraduction.isPending}
      />
    </div>
  );
}

// ─── Ligne vérification avec ses traductions ──────────────────────────────────
function VerifTraductionsRow({
  verif,
  onRefresh,
}: {
  verif: VerificationDto;
  onRefresh: () => void;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const { data: traductionsData, refetch } = useTraductionsVerification(verif.id);
  const deleteTraduction = useDeleteTraductionVerification();

  const [modal, setModal] = useState<{
    langue: string;
    langLabel: string;
    trad?: TraductionVerificationDto;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TraductionVerificationDto | null>(null);

  const tradsList: TraductionVerificationDto[] = traductionsData || verif.traductions || [];
  const verifAffirmation = verif.traductions?.[0]?.affirmation || verif.slug;
  const completion = Math.round((tradsList.length / LANGUES.length) * 100);

  const confirmDelete = () => {
    if (!deleteTarget?.id) return;
    deleteTraduction.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        refetch();
        onRefresh();
      },
      onError: () => {
        toast.error(t("admin.common.deleteError"));
        setDeleteTarget(null);
      },
    });
  };

  const handleSaved = () => { refetch(); onRefresh(); };

  return (
    <div className="border border-border rounded-md overflow-hidden">
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center gap-4 px-4 py-3 hover:bg-surface/60 text-left transition-colors"
      >
        <div className="shrink-0 h-7 w-7 rounded-md bg-surface flex items-center justify-center">
          <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-foreground line-clamp-1">« {verifAffirmation} »</div>
          <div className="flex items-center gap-2 mt-0.5">
            <VerdictBadge verdict={verif.verdict} />
            <span className="text-[10px] text-muted-foreground">·</span>
            <span className="text-[10px] text-muted-foreground font-mono">{verif.format}</span>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 shrink-0">
          <div className="w-20 h-1.5 rounded-full bg-surface overflow-hidden">
            <div className="h-full rounded-full bg-verified transition-all" style={{ width: `${completion}%` }} />
          </div>
          <span className="text-[10px] font-bold text-muted-foreground w-8 text-right">{completion}%</span>
        </div>
        <div className="flex gap-1 shrink-0">
          {LANGUES.map((l) => (
            <span
              key={l.code}
              className={`text-[9px] font-bold uppercase rounded px-1.5 py-0.5 ${
                tradsList.some((t) => t.langue?.toLowerCase() === l.code.toLowerCase())
                  ? "bg-verified/15 text-verified"
                  : "bg-surface text-muted-foreground/50"
              }`}
            >
              {l.code}
            </span>
          ))}
        </div>
        <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-border bg-surface/20 px-4 py-4">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            {LANGUES.map((l) => {
              const trad = tradsList.find((t) => t.langue?.toLowerCase() === l.code.toLowerCase());
              const hasIt = !!trad;
              return (
                <div key={l.code} className="rounded-md border border-border bg-background p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="text-base leading-none">{l.flag}</span>
                      <span className="text-xs font-semibold text-foreground">{l.label}</span>
                    </div>
                    <LangStatus hasIt={hasIt} />
                  </div>
                  {trad?.affirmation && (
                    <p className="text-[10px] text-muted-foreground line-clamp-2 leading-relaxed">{trad.affirmation}</p>
                  )}
                  {trad?.explicationCourte && !trad?.affirmation && (
                    <p className="text-[10px] text-muted-foreground italic line-clamp-2">{trad.explicationCourte}</p>
                  )}
                  <div className="flex gap-1">
                    {hasIt ? (
                      <>
                        <button
                          onClick={() => setModal({ langue: l.apiCode, langLabel: l.label, trad })}
                          className="flex-1 flex items-center justify-center gap-1 rounded-sm border border-border py-1 text-[10px] font-semibold text-muted-foreground hover:text-foreground hover:border-foreground transition-colors"
                        >
                          <Edit2 className="h-2.5 w-2.5" /> {t("admin.translations.action.edit")}
                        </button>
                        <button
                          onClick={() => setDeleteTarget(trad)}
                          className="rounded-sm border border-border p-1 text-muted-foreground hover:text-alert hover:border-alert/50 transition-colors"
                        >
                          <Trash2 className="h-2.5 w-2.5" />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setModal({ langue: l.apiCode, langLabel: l.label })}
                        className="w-full flex items-center justify-center gap-1 rounded-sm border border-dashed border-border py-1.5 text-[10px] font-semibold text-muted-foreground hover:text-foreground hover:border-foreground transition-colors"
                      >
                        <Plus className="h-3 w-3" /> {t("admin.translations.action.add")}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {modal && (
        <VerifTraductionModal
          verificationId={verif.id}
          verifAffirmation={verifAffirmation}
          langue={modal.langue}
          langLabel={modal.langLabel}
          initial={modal.trad}
          traductionId={modal.trad?.id}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t("admin.translations.deleteConfirm.title")}
        description={t("admin.translations.deleteConfirm.desc")}
        onConfirm={confirmDelete}
        loading={deleteTraduction.isPending}
      />
    </div>
  );
}

// ─── Page principale Traductions ──────────────────────────────────────────────
function Translations() {
  const { t } = useI18n();
  const [tab, setTab] = useState<TabType>("articles");
  const [search, setSearch] = useState("");

  const { data: articlesData, isLoading: loadingArticles, refetch: refetchArticles } = useArticles(0, 200);
  const { data: verifications, isLoading: loadingVerifs, refetch: refetchVerifs } = useVerifications();

  const articles: ArticleDto[] = articlesData?.content || articlesData || [];
  const verifs: VerificationDto[] = verifications || [];

  // ── Stats globales articles ──
  const totalArticlePossible = articles.length * LANGUES.length;
  const totalArticleTraduits = articles.reduce((acc, a) => acc + (a.languesDisponibles?.length || 0), 0);
  const articleCompletion = totalArticlePossible ? Math.round((totalArticleTraduits / totalArticlePossible) * 100) : 0;

  // ── Stats globales vérifications ──
  const totalVerifPossible = verifs.length * LANGUES.length;
  const totalVerifTraduits = verifs.reduce((acc, v) => acc + (v.traductions?.length || 0), 0);
  const verifCompletion = totalVerifPossible ? Math.round((totalVerifTraduits / totalVerifPossible) * 100) : 0;

  // ── Stats par langue (tab actif) ──
  const statsByLang = LANGUES.map((l) => {
    if (tab === "articles") {
      const count = articles.filter((a) =>
        (a.languesDisponibles || []).some((d) => d.toLowerCase() === l.code)
      ).length;
      return { ...l, count, total: articles.length };
    } else {
      const count = verifs.filter((v) =>
        (v.traductions || []).some((t) => t.langue?.toLowerCase() === l.code)
      ).length;
      return { ...l, count, total: verifs.length };
    }
  });

  // ── Filtrage ──
  const filteredArticles = articles.filter((a) => {
    if (!search) return true;
    const titre = a.traductions?.[0]?.titre?.toLowerCase() || "";
    return titre.includes(search.toLowerCase()) || a.slug.includes(search.toLowerCase());
  });

  const filteredVerifs = verifs.filter((v) => {
    if (!search) return true;
    const affirmation = v.traductions?.[0]?.affirmation?.toLowerCase() || "";
    return affirmation.includes(search.toLowerCase()) || v.slug.includes(search.toLowerCase());
  });

  const TABS = [
    { value: "articles" as const, label: t("search.articles"), count: articles.length, pct: articleCompletion },
    { value: "verifications" as const, label: t("admin.nav.factchecks"), count: verifs.length, pct: verifCompletion },
  ];

  return (
    <>
      <AdminTopbar
        title={t("admin.nav.translations")}
        subtitle={t("admin.translations.subtitle")}
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Tabs */}
        <div className="flex border-b border-border">
          {TABS.map((tab_item) => (
            <button
              key={tab_item.value}
              onClick={() => { setTab(tab_item.value); setSearch(""); }}
              className={`px-5 py-3 text-xs font-semibold uppercase tracking-wider transition-colors border-b-2 -mb-px flex items-center gap-2 ${
                tab === tab_item.value
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab_item.label}
              <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold ${tab === tab_item.value ? "bg-foreground text-background" : "bg-surface text-muted-foreground"}`}>
                {tab_item.count}
              </span>
              <span className="text-[10px] font-medium text-muted-foreground">{tab_item.pct}%</span>
            </button>
          ))}
        </div>

        {/* Stats par langue */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {statsByLang.map((l) => {
            const pct = l.total ? Math.round((l.count / l.total) * 100) : 0;
            return (
              <div key={l.code} className="rounded-md border border-border bg-card p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl leading-none">{l.flag}</span>
                  <div>
                    <p className="text-xs font-semibold text-foreground leading-none">{l.label}</p>
                    <p className="text-[10px] text-muted-foreground uppercase font-mono mt-0.5">{l.apiCode}</p>
                  </div>
                </div>
                <div className="flex items-end justify-between mb-1">
                  <span className="text-[10px] text-muted-foreground">
                    {tab === "articles" ? "Articles" : t("admin.nav.factchecks")}
                  </span>
                  <span className="text-xs font-bold text-foreground">{l.count}/{l.total}</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-surface overflow-hidden">
                  <div className="h-full rounded-full bg-verified" style={{ width: `${pct}%` }} />
                </div>
                <p className="text-[10px] text-muted-foreground mt-1 text-right font-semibold">{pct}%</p>
              </div>
            );
          })}
        </div>

        {/* Recherche */}
        <div className="relative max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tab === "articles" ? t("admin.translations.filter.articles") : t("admin.translations.filter.verifs")}
            className="w-full rounded-md border border-border bg-surface pl-8 pr-8 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* ── Contenu Articles ── */}
        {tab === "articles" && (
          <ModulePanel
            title={`${t("search.articles")} (${filteredArticles.length})`}
            description={t("admin.translations.desc.articles")}
          >
            {loadingArticles ? (
              <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">{t("admin.common.loading")}</span>
              </div>
            ) : filteredArticles.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
                <BookOpen className="h-8 w-8 opacity-30" />
                <p className="text-sm">{search ? t("admin.translations.noResult") : t("admin.translations.noArticle")}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredArticles.map((a) => (
                  <ArticleTraductionsRow key={a.id} article={a} onRefresh={refetchArticles} />
                ))}
              </div>
            )}
          </ModulePanel>
        )}

        {/* ── Contenu Vérifications ── */}
        {tab === "verifications" && (
          <ModulePanel
            title={`${t("admin.nav.factchecks")} (${filteredVerifs.length})`}
            description={t("admin.translations.desc.verifs")}
          >
            {loadingVerifs ? (
              <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">{t("admin.common.loading")}</span>
              </div>
            ) : filteredVerifs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
                <ShieldCheck className="h-8 w-8 opacity-30" />
                <p className="text-sm">{search ? t("admin.translations.noResult") : t("admin.translations.noVerif")}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredVerifs.map((v) => (
                  <VerifTraductionsRow key={v.id} verif={v} onRefresh={refetchVerifs} />
                ))}
              </div>
            )}
          </ModulePanel>
        )}
      </div>
    </>
  );
}

export default Translations;

